/**
 * Create form for a program.
 */
import * as React from 'react';
import { FileText, Clock } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { Input } from '@/components/forms/Input';
import { Select } from '@/components/forms/Select';
import { DatePicker } from '@/components/forms/DatePicker';
import { AvailabilityGrid } from '@/components/forms/AvailabilityGrid';
import { createProgram } from '@/services/programs.service';
import type { CreateProgramDto } from '@/types/program.types';
import { useFormValidation } from '@/hooks/useFormValidation';
import { programValidationSchema } from '@/utils/validation-schemas-utils';
import { SlidePanelForm } from '@/components/common/SlidePanelForm';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import { useMode } from '@/contexts/ModeContext';
import { PROGRAM_COURSE_PATH_FORM_OPTIONS, PROGRAM_STATUS_FORM_OPTIONS } from './program-configs';
import {
  applyProgramFormField,
  buildProgramSubmitDto,
  emptyCreateProgramForm,
  mapProgramTypeOptions,
} from './program-form-utils';

/** Props for {@link CreateProgramView}. */
interface CreateProgramViewProps {
  onClose: () => void;
  onSuccess?: () => void;
}

/** Create form for a program. */
export const CreateProgramView: React.FC<CreateProgramViewProps> = ({ onClose, onSuccess }) => {
  const { mode } = useMode();
  /** Type options filtered by the current app mode. */
  const typeOptions = React.useMemo(() => mapProgramTypeOptions(mode), [mode]);
  const [formData, setFormData] = React.useState<CreateProgramDto>(emptyCreateProgramForm);

  const validation = useFormValidation(programValidationSchema);

  const createMutation = useMutation({
    mutationFn: (dto: CreateProgramDto) => createProgram(dto),
    onSuccess: () => {
      toast.success('Programme créé avec succès !');
      onSuccess?.();
      onClose();
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error));
    },
  });

  const isSubmitting = createMutation.isPending;

  /** Patch one field and re-validate. */
  const handleFieldChange = (
    field: keyof CreateProgramDto,
    value:
      | string
      | number
      | { weekly: Array<{ day: number; start: string; end: string }> }
      | null
      | undefined,
  ) => {
    const updatedData = applyProgramFormField(formData, field, value);
    setFormData(updatedData);
    validation.validateField(field, value, updatedData);
    validation.markAsTouched(field);
    validation.markAsDirty(field);
  };

  const handleBlur = (field: string) => {
    validation.markAsTouched(field);
  };

  /** Validate then POST create DTO. */
  const handleSubmit = () => {
    if (!validation.validateForm(formData)) return;
    createMutation.mutate(buildProgramSubmitDto(formData));
  };

  return (
    <SlidePanelForm
      formType="create"
      title="Créer un programme"
      subtitle="Renseignez les informations du programme"
      onClose={onClose}
      onSubmit={handleSubmit}
      submitLabel="Créer"
      isSubmitting={isSubmitting}
      isValid={validation.isFormValid()}
    >
      <div className="form-section">
        <h4 className="form-section-title">
          <FileText className="w-4 h-4" />
          Informations générales
        </h4>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Code"
            value={formData.code}
            onChange={(e) => handleFieldChange('code', e.target.value)}
            onBlur={() => handleBlur('code')}
            placeholder="Ex: BAC_GEN"
            maxLength={50}
            required
            helperText="Code unique identifiant le programme"
            state={validation.getFieldState('code')}
            error={validation.getFieldError('code')}
          />

          <Input
            label="Libellé"
            value={formData.name}
            onChange={(e) => handleFieldChange('name', e.target.value)}
            onBlur={() => handleBlur('name')}
            placeholder="Ex: Baccalauréat Général"
            maxLength={255}
            required
            helperText="Nom complet du programme"
            state={validation.getFieldState('name')}
            error={validation.getFieldError('name')}
          />
        </div>

        <Input
          label="Description"
          value={formData.description || ''}
          onChange={(e) => handleFieldChange('description', e.target.value)}
          onBlur={() => handleBlur('description')}
          placeholder="Ex: Préparation au baccalauréat"
          maxLength={500}
          state={validation.getFieldState('description')}
          error={validation.getFieldError('description')}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <DatePicker
            label="Date de début"
            value={formData.startDate || ''}
            onChange={(value) => handleFieldChange('startDate', value)}
            onBlur={() => handleBlur('startDate')}
            state={validation.getFieldState('startDate')}
            error={validation.getFieldError('startDate')}
          />

          <DatePicker
            label="Date de fin"
            value={formData.endDate || ''}
            onChange={(value) => handleFieldChange('endDate', value)}
            onBlur={() => handleBlur('endDate')}
            minDate={formData.startDate}
            state={validation.getFieldState('endDate')}
            error={validation.getFieldError('endDate')}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="Statut"
            options={PROGRAM_STATUS_FORM_OPTIONS}
            value={formData.status || 'draft'}
            onChange={(value) => handleFieldChange('status', value)}
            onBlur={() => handleBlur('status')}
            helperText="Brouillon: modifiable. Actif: visible. Suspendu: masqué. Clôturé: archivé."
            state={validation.getFieldState('status')}
            error={validation.getFieldError('status')}
          />

          <Select
            label="Type"
            options={typeOptions}
            value={formData.type || ''}
            onChange={(value) => handleFieldChange('type', value)}
            onBlur={() => handleBlur('type')}
            placeholder="Sélectionner un type"
            helperText="Type de modalité d'accompagnement"
            state={validation.getFieldState('type')}
            error={validation.getFieldError('type')}
          />
        </div>

        <Select
          label="Parcours"
          options={PROGRAM_COURSE_PATH_FORM_OPTIONS}
          value={formData.coursePathType || ''}
          onChange={(value) => handleFieldChange('coursePathType', value)}
          onBlur={() => handleBlur('coursePathType')}
          placeholder="Sélectionner un parcours"
          helperText="Parcours classique ou club de sport"
          required
          state={validation.getFieldState('coursePathType')}
          error={validation.getFieldError('coursePathType')}
        />
      </div>

      <div className="form-section">
        <h4 className="form-section-title">
          <Clock className="w-4 h-4" />
          Disponibilités
        </h4>
        <AvailabilityGrid
          value={formData.availability ?? { weekly: [] }}
          onChange={(value) => handleFieldChange('availability', value)}
          disabled={false}
        />
        <p className="text-xs text-muted-foreground mt-2">
          Définissez les créneaux horaires hebdomadaires disponibles pour ce programme
        </p>
      </div>
    </SlidePanelForm>
  );
};
